import React, { useCallback, useEffect, useMemo, useState } from "react";

import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HistoryIcon from "@mui/icons-material/History";
import ViewInArOutlinedIcon from "@mui/icons-material/ViewInArOutlined";
import WebhookIcon from "@mui/icons-material/Webhook";
import {
    Box,
    Card,
    CardContent,
    Chip,
    FormControl,
    Grid,
    MenuItem,
    Select,
    Stack,
    Tooltip,
} from "@mui/material";

import { Spinner } from "react-bootstrap";
import ReactDatePicker from "react-datepicker";
import { useNavigate, useParams } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { DateRange } from "types/app/date";

import { getDestinationIcon, getSourceIcon } from "helpers/leads/icon";
import { getDuplicateLeads, getFailedLeads } from "helpers/leads/reports";

import LabelValue from "../../components/atoms/LabelValue";
import PageContainer from "../../components/atoms/PageContainer";
import StandartButton from "../../components/atoms/StandartButton";
import SourcePopover from "../../components/molecules/SourcePopover";
import { ButtonVariant } from "../../Constant/button";
import { parseDate, substractDate } from "../../Constant/date";
import { leadConnectionHistoryPath, routes } from "../../Constant/routes";
import { getInputValueOnChange } from "../../helpers/form";
import useUpdateEffect from "../../hooks/useUpdateEffect";
import FailedLeadsModal from "./FailedLeadsModal";
import {
    buildDevMockDuplicateLeadsResponse,
    buildDevMockFailedLeadsResponse,
    withDevMockLeads,
} from "./FailedLeadsModal/FailedLeadsModal.mock";
import {
    parseConnectionReport,
    parseSourceMapping,
} from "./LeadConnectionReportsPage.helpers";
import * as Styled from "./LeadConnectionReportsPage.styled";
import { LeadConnectionReportsPageProps } from "./LeadConnectionReportsPage.types";
import ReportChart from "./ReportChart";

/**
 * TODO: Filter component should be abstracted to generic component
 */
const LeadConnectionReportsPage = ({
    //state
    language,
    connectionReport,
    isReportsLoading,
    sourceMapping,

    //dispatch
    dispatchGetConnectionReports,
    dispatchClearConnectionReports,
    dispatchGetConnectionSourceMapping,
    dispatchClearSourceMapping,
}: LeadConnectionReportsPageProps) => {
    const [isLoadingFailedLeads, setIsLoadingFailedLeads] = useState(false);
    const [failedLeads, setFailedLeads] = useState([]);
    const [duplicateLeads, setDuplicateLeads] = useState([]);
    const [isLoadingDuplicateLeads, setIsLoadingDuplicateLeads] =
        useState(false);
    const [isFailedLeadsModalOpen, setIsFailedLeadsModalOpen] = useState(false);
    const [failedLeadsPagination, setFailedLeadsPagination] = useState({
        currentPage: 1,
        pageSize: 10,
        total: 0,
        lastPage: 1,
    });

    const [duplicateLeadsPagination, setDuplicateLeadsPagination] = useState({
        currentPage: 1,
        pageSize: 10,
        total: 0,
        lastPage: 1,
    });

    const [sourceCardRef, setSourceCardRef] = useState(null);
    const [isSourcePopoverVisible, setIsSourcePopoverVisible] = useState(false);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [dateRange, setDateRange] = useState<DateRange | null>(
        DateRange.last7Days,
    );

    const navigate = useNavigate();
    const params = useParams();
    const id = params?.id;

    const currentDate = useMemo(() => new Date(), []);

    const isSingularDay = useMemo(() => {
        return (
            dateRange === DateRange.today ||
            endDate?.getDate() - startDate?.getDate() === 0
        );
    }, [dateRange, startDate, endDate]);

    const { sumData, stats, hourlyStats } = useMemo(
        () => parseConnectionReport(connectionReport),
        [connectionReport],
    );

    const sourceAndDestination = useMemo(
        () => parseSourceMapping(sourceMapping),
        [sourceMapping],
    );

    const fetchConnectionReports = useCallback(
        (startDate, endDate) => {
            dispatchGetConnectionReports({
                id: id as string,
                start_date: startDate,
                end_date: endDate,
            });
        },
        [id, dispatchGetConnectionReports],
    );

    // Handle start date change - clear end date if start date > end date
    const handleStartDateChange = useCallback(
        (date) => {
            setStartDate(date);

            if (date && endDate && date > endDate) {
                setEndDate(null);
            }
        },
        [endDate],
    );

    // Custom date range - only fetch when BOTH dates are selected
    useUpdateEffect(() => {
        if (startDate && endDate) {
            const localStartDate = new Date(startDate);
            const localEndDate = new Date(endDate);
            // local iso problem
            localStartDate.setDate(localStartDate.getDate() + 1);
            localEndDate.setDate(localEndDate.getDate() + 1);

            // Set dateRange to empty only when both dates are set
            setDateRange(DateRange.empty);

            fetchConnectionReports(
                parseDate(localStartDate),
                parseDate(localEndDate),
            );
        }
    }, [startDate, endDate]);

    // Preset date range selection
    useEffect(() => {
        if (dateRange !== DateRange.empty) {
            // Clear custom dates when a preset is selected
            if (startDate) setStartDate(null);
            if (endDate) setEndDate(null);

            const startDateValue = substractDate(dateRange);
            // For "Today" and "Yesterday", end_date should be the same as start_date
            const endDateValue =
                dateRange === DateRange.today ||
                dateRange === DateRange.yesterday
                    ? startDateValue
                    : parseDate(currentDate);

            fetchConnectionReports(startDateValue, endDateValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange, fetchConnectionReports, currentDate]);

    const handleGetFailedLeads = useCallback(
        async (page = 1, pageSize = 10) => {
            setIsLoadingFailedLeads(true);
            const response = withDevMockLeads(
                await getFailedLeads(id, page, pageSize),
                () => buildDevMockFailedLeadsResponse(page, pageSize),
            );
            if (response.status) {
                setFailedLeads(response.data.leads);
                setFailedLeadsPagination({
                    currentPage: response.data.pagination.current_page,
                    pageSize: response.data.pagination.per_page,
                    total: response.data.pagination.total,
                    lastPage: response.data.pagination.last_page,
                });
            }
            setIsLoadingFailedLeads(false);
        },
        [id, setFailedLeadsPagination, setFailedLeads],
    );

    const handleGetDuplicateLeads = useCallback(
        async (page = 1, pageSize = 10) => {
            setIsLoadingDuplicateLeads(true);
            const response = withDevMockLeads(
                await getDuplicateLeads(id, page, pageSize),
                () => buildDevMockDuplicateLeadsResponse(page, pageSize),
            );
            if (response.status) {
                setDuplicateLeads(response.data.leads);
                setDuplicateLeadsPagination({
                    currentPage: response.data.pagination.current_page,
                    pageSize: response.data.pagination.per_page,
                    total: response.data.pagination.total,
                    lastPage: response.data.pagination.last_page,
                });
            }
            setIsLoadingDuplicateLeads(false);
        },
        [id, setDuplicateLeadsPagination, setDuplicateLeads],
    );

    // No id route badk
    useEffect(() => {
        !id && navigate(routes.connection);
        if (id) {
            handleGetFailedLeads();
            handleGetDuplicateLeads();
            dispatchGetConnectionSourceMapping({ id });
        }

        return () => {
            dispatchClearConnectionReports();
            dispatchClearSourceMapping();
        };
    }, [
        id,
        navigate,
        handleGetFailedLeads,
        handleGetDuplicateLeads,
        dispatchClearConnectionReports,
        dispatchGetConnectionSourceMapping,
        dispatchClearSourceMapping,
    ]);

    return (
        <PageContainer
            loading={
                isReportsLoading ||
                ((isLoadingFailedLeads || isLoadingDuplicateLeads) &&
                    !isFailedLeadsModalOpen &&
                    !failedLeads.length &&
                    !duplicateLeads.length)
            }
            containerClassName="tracker-detail-area"
            additionalButtons={
                <Stack direction="row" gap={1} alignItems="center">
                    <div className="position-relative overflow-hidden z-1">
                        {isLoadingFailedLeads && (
                            <div className="circle-loader">
                                <ClipLoader color={"#000"} size={17} />
                            </div>
                        )}
                        <Tooltip
                            title={
                                !failedLeads?.length &&
                                "There is no undelivered leads."
                            }
                        >
                            <Box>
                                <StandartButton
                                    onClick={() =>
                                        setIsFailedLeadsModalOpen(true)
                                    }
                                    {...(failedLeads?.length ||
                                    duplicateLeads?.length
                                        ? {
                                              variant:
                                                  ButtonVariant.DANGER_OUTLINED,
                                          }
                                        : {
                                              variant:
                                                  ButtonVariant.BLACK_OUTLINED,
                                          })}
                                    disabled={
                                        !failedLeads?.length &&
                                        !duplicateLeads?.length
                                    }
                                >
                                    <Stack
                                        direction="row"
                                        gap={1}
                                        alignItems="center"
                                    >
                                        <ErrorOutlineIcon />
                                        <Box>Failed Leads</Box>
                                    </Stack>
                                </StandartButton>
                            </Box>
                        </Tooltip>
                    </div>
                    <StandartButton
                        {...(id ? { to: leadConnectionHistoryPath(id) } : {})}
                        disabled={!id}
                        variant={ButtonVariant.BLACK_OUTLINED}
                    >
                        <Stack direction="row" gap={1} alignItems="center">
                            <HistoryIcon />
                            <Box>History</Box>
                        </Stack>
                    </StandartButton>
                </Stack>
            }
        >
            <Box>
                {isFailedLeadsModalOpen && (
                    <FailedLeadsModal
                        destination={sourceAndDestination?.destinationType}
                        failedLeads={failedLeads}
                        duplicateLeads={duplicateLeads}
                        onClose={() => setIsFailedLeadsModalOpen(false)}
                        handleGetFailedLeads={handleGetFailedLeads}
                        handleGetDuplicateLeads={handleGetDuplicateLeads}
                        isLoadingFailedLeads={isLoadingFailedLeads}
                        failedLeadsPagination={failedLeadsPagination}
                        duplicateLeadsPagination={duplicateLeadsPagination}
                        setDuplicateLeadsPagination={
                            setDuplicateLeadsPagination
                        }
                        setFailedLeadsPagination={setFailedLeadsPagination}
                    />
                )}
                <Grid container>
                    <Grid
                        size={{
                            xs: 12,
                            md: 6,
                        }}
                        ref={setSourceCardRef}
                        position={"relative"}
                        zIndex={999}
                    >
                        <Stack gap={2}>
                            {sourceAndDestination?.sourceType && (
                                <LabelValue
                                    label="Source"
                                    value={
                                        <Styled.SourceDestinationContainer>
                                            <img
                                                src={getSourceIcon(
                                                    sourceAndDestination?.sourceIconCode,
                                                )}
                                                alt={
                                                    sourceAndDestination?.sourceIconCode
                                                }
                                            />
                                            <Box>
                                                {
                                                    sourceAndDestination?.sourceType
                                                }
                                            </Box>
                                        </Styled.SourceDestinationContainer>
                                    }
                                    gap={1}
                                />
                            )}
                            <Stack
                                direction="row"
                                alignItems="flex-end"
                                className="connection-detail-stack"
                            >
                                <Stack gap={2}>
                                    {!!sourceAndDestination?.sourceName && (
                                        <LabelValue
                                            label="Name"
                                            value={
                                                sourceAndDestination?.sourceName
                                            }
                                        />
                                    )}
                                    {!!sourceAndDestination?.sourcePageNames
                                        ?.length && (
                                        <LabelValue
                                            label={
                                                sourceAndDestination?.sourceIconCode ===
                                                "lnkdn"
                                                    ? "Ad Account"
                                                    : "Page Name(s)"
                                            }
                                            value={
                                                <Stack
                                                    gap={1}
                                                    direction="row"
                                                    flexWrap="wrap"
                                                >
                                                    {sourceAndDestination?.sourcePageNames?.map(
                                                        (form, index) =>
                                                            index < 3 && (
                                                                <Chip
                                                                    className="chip-leads-page"
                                                                    label={form}
                                                                    style={{
                                                                        width: "fit-content",
                                                                    }}
                                                                    onClick={() => {}}
                                                                />
                                                            ),
                                                    )}
                                                    {sourceAndDestination
                                                        ?.sourcePageNames
                                                        ?.length > 3 && (
                                                        <Chip
                                                            className="chip-more chip-leads-page"
                                                            onClick={() => {}}
                                                            label={
                                                                <Stack direction="row">
                                                                    <Box>
                                                                        {sourceAndDestination
                                                                            ?.sourcePageNames
                                                                            .length -
                                                                            3}
                                                                    </Box>
                                                                    <Box>
                                                                        + More
                                                                    </Box>
                                                                </Stack>
                                                            }
                                                        />
                                                    )}
                                                </Stack>
                                            }
                                        />
                                    )}
                                    {!!sourceAndDestination?.sourceFormNames
                                        ?.length && (
                                        <LabelValue
                                            label="Form Name(s)"
                                            value={
                                                <Stack
                                                    gap={1}
                                                    direction="row"
                                                    flexWrap="wrap"
                                                >
                                                    {sourceAndDestination?.sourceFormNames?.map(
                                                        (form, index) =>
                                                            index < 3 && (
                                                                <Chip
                                                                    className="chip-leads-page"
                                                                    onClick={() => {}}
                                                                    label={form}
                                                                    style={{
                                                                        width: "fit-content",
                                                                    }}
                                                                />
                                                            ),
                                                    )}
                                                    {sourceAndDestination
                                                        ?.sourceFormNames
                                                        ?.length > 3 && (
                                                        <Chip
                                                            className="chip-more chip-leads-page"
                                                            onClick={() => {}}
                                                            label={
                                                                <Stack direction="row">
                                                                    <Box>
                                                                        {sourceAndDestination
                                                                            ?.sourceFormNames
                                                                            .length -
                                                                            3}
                                                                    </Box>
                                                                    <Box>
                                                                        + More
                                                                    </Box>
                                                                </Stack>
                                                            }
                                                        />
                                                    )}
                                                </Stack>
                                            }
                                        />
                                    )}
                                </Stack>
                                <Box className="lead-reports-expand-button">
                                    {sourceAndDestination?.sourceFormNames
                                        ?.length > 3 && (
                                        <StandartButton
                                            variant={ButtonVariant.ICON_BUTTON}
                                            rounded
                                            onClick={() =>
                                                setIsSourcePopoverVisible(
                                                    !isSourcePopoverVisible,
                                                )
                                            }
                                        >
                                            {isSourcePopoverVisible ? (
                                                <KeyboardArrowUp />
                                            ) : (
                                                <KeyboardArrowDown />
                                            )}
                                        </StandartButton>
                                    )}
                                </Box>
                            </Stack>
                        </Stack>
                        <Box className="tracker-detail-area" padding={0}>
                            <SourcePopover
                                visible={isSourcePopoverVisible}
                                ref={sourceCardRef}
                                sourceIcon={getSourceIcon(
                                    sourceAndDestination?.sourceIconCode,
                                )}
                                sourceName={sourceAndDestination?.sourceType}
                                isFacebook={
                                    sourceAndDestination?.sourceIconCode ===
                                        "fl" ||
                                    sourceAndDestination?.sourceIconCode ===
                                        "Facebook Lead"
                                }
                                isTiktok={
                                    sourceAndDestination?.sourceIconCode ===
                                        "tiktok" ||
                                    sourceAndDestination?.sourceIconCode ===
                                        "Tiktok Lead Generation"
                                }
                                pages={sourceAndDestination?.sourcePageNames}
                                name={sourceAndDestination?.sourceName}
                                forms={sourceAndDestination?.sourceFormNames}
                                pagesLabel={
                                    sourceAndDestination?.sourceIconCode ===
                                    "lnkdn"
                                        ? "Ad Account"
                                        : "Page Name(s)"
                                }
                                formsLabel={"Form Name(s)"}
                            />
                        </Box>
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            md: 6,
                        }}
                    >
                        <Stack gap={2}>
                            {sourceAndDestination?.destinationType && (
                                <LabelValue
                                    label="Destination"
                                    value={
                                        <Styled.SourceDestinationContainer>
                                            <img
                                                src={getDestinationIcon(
                                                    sourceAndDestination?.destinationIconCode,
                                                )}
                                                alt={
                                                    sourceAndDestination?.destinationIconCode
                                                }
                                            />
                                            <Box>
                                                {
                                                    sourceAndDestination?.destinationType
                                                }
                                            </Box>
                                        </Styled.SourceDestinationContainer>
                                    }
                                    gap={1}
                                />
                            )}
                            {sourceAndDestination?.destinationName && (
                                <LabelValue
                                    label="Name"
                                    value={
                                        sourceAndDestination?.destinationName
                                    }
                                />
                            )}
                        </Stack>
                    </Grid>
                </Grid>
            </Box>
            <Stack>
                {/** Filter card */}
                <Box>
                    <Card className="mt-5 header-card">
                        <CardContent className="tracker-reports-header">
                            <div className="total-events-area">
                                <div className="left">
                                    <WebhookIcon></WebhookIcon>
                                </div>
                                <div className="right">
                                    <span>
                                        {language?.tracker_detail
                                            ?.total_leads ?? "Total Leads"}
                                    </span>
                                    {isReportsLoading && connectionReport ? (
                                        <Spinner />
                                    ) : (
                                        <strong>{sumData?.totalLeads}</strong>
                                    )}
                                </div>
                            </div>
                            <div>
                                <div className="mui-datepicker">
                                    <label>
                                        {language?.tracker_detail?.start_date ??
                                            "Start Date"}
                                    </label>
                                    <ReactDatePicker
                                        className="pl-1 mr-2 datePicker"
                                        selected={startDate}
                                        selectsStart
                                        placeholderText="dd/mm/yyyy"
                                        onChange={handleStartDateChange}
                                        dateFormat="dd/MM/yyyy"
                                        maxDate={currentDate}
                                        disabled={
                                            isReportsLoading && connectionReport
                                        }
                                    />
                                    <DateRangeOutlinedIcon></DateRangeOutlinedIcon>
                                </div>
                            </div>
                            <div>
                                <div className="mui-datepicker">
                                    <label>
                                        {language?.tracker_detail?.end_date ??
                                            "End Date"}
                                    </label>
                                    <ReactDatePicker
                                        className="pl-1 datePicker"
                                        selected={endDate}
                                        selectsEnd
                                        placeholderText="dd/mm/yyyy"
                                        onChange={setEndDate}
                                        dateFormat="dd/MM/yyyy"
                                        minDate={startDate}
                                        maxDate={currentDate}
                                        disabled={
                                            isReportsLoading && connectionReport
                                        }
                                    />
                                    <DateRangeOutlinedIcon></DateRangeOutlinedIcon>
                                </div>
                            </div>
                            <div>
                                <span className="or-text">
                                    {language?.tracker_detail?.or ?? "OR"}
                                </span>
                            </div>

                            <div>
                                <FormControl fullWidth>
                                    <Select
                                        label={
                                            language?.tracker_detail
                                                ?.date_range ?? "Date Range"
                                        }
                                        value={dateRange}
                                        onChange={(e) =>
                                            setDateRange(
                                                getInputValueOnChange(e),
                                            )
                                        }
                                        style={{ width: 200 }}
                                        disabled={isReportsLoading}
                                    >
                                        <MenuItem value={DateRange.empty}>
                                            {language?.reports?.select_days ??
                                                "Select Days"}{" "}
                                        </MenuItem>
                                        <MenuItem value={DateRange.yesterday}>
                                            {language?.reports?.yesterday ??
                                                "Yesterday"}{" "}
                                        </MenuItem>
                                        <MenuItem value={DateRange.today}>
                                            {language?.reports?.today ??
                                                "Today"}{" "}
                                        </MenuItem>
                                        <MenuItem value={DateRange.last7Days}>
                                            {language?.reports?.last_7_days ??
                                                "Last 7 days"}{" "}
                                        </MenuItem>
                                        <MenuItem value={DateRange.last28Days}>
                                            {language?.reports?.last_28_days ??
                                                "Last 28 days"}{" "}
                                        </MenuItem>
                                        <MenuItem value={DateRange.last3Months}>
                                            {language?.reports?.last_90_days ??
                                                "Last 90 days"}{" "}
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </div>
                        </CardContent>
                    </Card>
                </Box>
                <Box>
                    <Card
                        className="mt-4"
                        style={{ boxShadow: "none", padding: 0 }}
                    >
                        <CardContent style={{ padding: 0 }}>
                            <ReportChart
                                isLoading={isReportsLoading && connectionReport}
                                stats={stats}
                                isSingularDay={isSingularDay}
                                hourlyStats={hourlyStats}
                            />
                        </CardContent>
                    </Card>
                </Box>

                <Box>
                    <Card className="mt-4 header-card">
                        <CardContent className="tracker-reports-header">
                            <div className="total-events-area">
                                <div className="left">
                                    <ViewInArOutlinedIcon></ViewInArOutlinedIcon>
                                </div>
                                <div className="right">
                                    <span>
                                        {language?.tracker_detail
                                            ?.total_leads ?? "Incoming Leads"}
                                    </span>
                                    {isReportsLoading && connectionReport ? (
                                        <Spinner />
                                    ) : (
                                        <strong>
                                            {sumData?.incomingLeads}
                                        </strong>
                                    )}
                                </div>
                            </div>
                            <div className="total-events-area">
                                <div className="left">
                                    <ViewInArOutlinedIcon></ViewInArOutlinedIcon>
                                </div>
                                <div className="right">
                                    <span>
                                        {language?.tracker_detail
                                            ?.total_leads ?? "Outgoing Leads"}
                                    </span>
                                    {isReportsLoading && connectionReport ? (
                                        <Spinner />
                                    ) : (
                                        <strong>
                                            {sumData?.outgoingLeads}
                                        </strong>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </Box>
            </Stack>
        </PageContainer>
    );
};

export default LeadConnectionReportsPage;
